/**
 * Responsibility: Implement atlas check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"
import { buildAtlasComponentReport } from '../../atlas-graph'
import { locationKey } from './common'

function hasNode(nodes: Record<string, unknown>, x: number, y: number, z = 0): boolean {
  return !!nodes[`${x}_${y}_${z}`]
}

export const atlasCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []
  const nodes = state.atlas?.nodes ?? {}
  const playerNodeKey = locationKey(state)
  const report = buildAtlasComponentReport(state.atlas, playerNodeKey)

  if (!nodes[playerNodeKey]) {
    incidents.push({
      code: 'atlas_player_node_missing',
      domain: 'atlas',
      severity: 'high',
      confidence: 0.99,
      message: `Atlas node for player location (${playerNodeKey}) is missing.`,
      remediationPlan: 'Create the missing atlas node from current room data.',
      context: { playerNodeKey },
    })
  }

  let invalidEdges = 0
  for (const edge of state.atlas?.edges ?? []) {
    const left = hasNode(nodes, edge.x1, edge.y1, edge.z1 ?? 0)
    const right = hasNode(nodes, edge.x2, edge.y2, edge.z2 ?? 0)
    if (!left || !right) invalidEdges += 1
  }
  if (invalidEdges > 0) {
    incidents.push({
      code: 'atlas_invalid_edges',
      domain: 'atlas',
      severity: 'medium',
      confidence: 0.98,
      message: `${invalidEdges} atlas edges reference missing nodes.`,
      remediationPlan: 'Prune edges that point to non-existent nodes.',
      context: { invalidEdges },
    })
  }

  const roomName = String(state.currentRoom?.name ?? '').trim()
  if (!roomName) {
    incidents.push({
      code: 'atlas_current_room_name_missing',
      domain: 'atlas',
      severity: 'medium',
      confidence: 0.94,
      message: 'Current room has no name.',
      remediationPlan: 'Set current room name from atlas node or fallback label.',
    })
  }

  let orphanedWorldNpcLocations = 0
  for (const npc of state.world?.npcs ?? []) {
    const key = String(npc?.location ?? '').trim()
    if (!key || !nodes[key]) orphanedWorldNpcLocations += 1
  }

  if (orphanedWorldNpcLocations > 0) {
    incidents.push({
      code: 'atlas_world_npc_location_missing_node',
      domain: 'atlas',
      severity: 'medium',
      confidence: 0.92,
      message: `${orphanedWorldNpcLocations} world NPC locations do not map to atlas nodes.`,
      remediationPlan: 'Relocate orphaned NPCs to the player node.',
      context: { orphanedWorldNpcLocations, playerNodeKey },
    })
  }

  const reachableNodeCount = report.reachableKeys.length
  const hasSealedReachableLoop = reachableNodeCount >= 3
    && report.frontierKeys.length === 0
    && report.loopKeys.length === reachableNodeCount

  if (hasSealedReachableLoop) {
    incidents.push({
      code: 'atlas_component_sealed',
      domain: 'atlas',
      severity: 'high',
      confidence: 0.9,
      message: 'Reachable atlas component has no frontier and is sealed into a loop.',
      remediationPlan: 'Open a new frontier route from the reachable atlas component.',
      context: {
        playerNodeKey,
        reachableNodeCount,
        loopKeys: report.loopKeys,
        suggestedExpansion: report.suggestedExpansion,
        activeQuestCount: (state.journal ?? []).filter((quest) => quest?.status === 'active').length,
      },
    })
  }

  const longEdges = (state.atlas?.edges ?? []).filter(edge => {
    const dx = Math.abs(edge.x1 - edge.x2)
    const dy = Math.abs(edge.y1 - edge.y2)
    const dz = Math.abs((edge.z1 ?? 0) - (edge.z2 ?? 0))
    return (dx + dy + dz) > 1
  })

  if (longEdges.length > 0) {
    incidents.push({
      code: 'atlas_long_passages',
      domain: 'atlas',
      severity: 'medium',
      confidence: 0.95,
      message: `${longEdges.length} atlas edges are too long and require segmentation.`,
      remediationPlan: 'Insert intermediate waypoint nodes into long atlas edges.',
      context: { longEdgeCount: longEdges.length },
    })
  }

  return { passed: incidents.length === 0, incidents }
}
